import { differenceInCalendarDays } from "date-fns";
import { hrmsGet, essGet, essPostBlob, essPost, essPut } from "../api/api";
import { downloadBlob, parseContentDispositionFilename } from "../utils/downloadFile";
import { extractLeaveRecords } from "../lib/leaveRecords";
import sessionDB from "../utils/sessionDB";

function unwrapList(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.content)) return response.data.content;
  if (Array.isArray(response?.content)) return response.content;
  return [];
}

function unwrapLeaveTypes(response) {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) return payload;
  return unwrapList(
    payload?.leaveTypes ??
      payload?.activeLeaveTypes ??
      payload?.content ??
      payload
  );
}

function isActiveLeaveType(item) {
  if (!item || typeof item !== "object") return false;

  const flag = item.isActive ?? item.active ?? item.enabled;
  if (
    flag === false ||
    flag === 0 ||
    flag === "0" ||
    flag === "N" ||
    flag === "n" ||
    String(flag).toLowerCase() === "false" ||
    String(flag).toLowerCase() === "inactive"
  ) {
    return false;
  }

  const status = String(item.status ?? item.leaveStatus ?? "").toUpperCase();
  if (status === "INACTIVE" || status === "DISABLED") {
    return false;
  }

  return true;
}

function unwrapLeaveBalances(response) {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) return payload;
  return unwrapList(
    payload?.balances ??
      payload?.leaveBalances ??
      payload?.leaveBalanceList ??
      payload?.content ??
      payload
  );
}

function resolveEmpId(empId) {
  return toOptionalId(empId) ?? toOptionalId(sessionDB.getEmpId());
}

function parseDurationValue(value) {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function mapLeaveType(item) {
  return {
    typeId: item.typeId ?? item.leaveTypeId ?? item.id,
    leaveName: item.leaveName ?? item.leaveTypeName ?? item.typeName ?? item.name,
    leaveCode: item.leaveCode ?? item.leaveTypeCode ?? item.code,
    totalDays: item.totalDays ?? item.allocatedDays ?? 0,
  };
}

function mapLeaveBalance(item) {
  const total =
    parseDurationValue(item.totalDays) ??
    parseDurationValue(item.total) ??
    parseDurationValue(item.allocatedDays) ??
    parseDurationValue(item.allocated);

  const leavesApproved =
    parseDurationValue(item.leavesApproved) ??
    parseDurationValue(item.usedDays) ??
    parseDurationValue(item.used) ??
    parseDurationValue(item.availedDays) ??
    0;

  const available =
    parseDurationValue(item.availableDays) ??
    parseDurationValue(item.available) ??
    parseDurationValue(item.balanceDays) ??
    parseDurationValue(item.balance) ??
    parseDurationValue(item.remainingDays) ??
    parseDurationValue(item.pendingBalance) ??
    (total != null ? Math.max(0, total - leavesApproved) : null);

  return {
    leaveTypeId: item.leaveTypeId ?? item.typeId ?? item.id,
    leaveName: item.leaveName ?? item.leaveTypeName ?? item.name,
    leaveCode: item.leaveCode ?? item.leaveTypeCode ?? item.code,
    leavesApproved,
    available,
    total,
    used: leavesApproved,
  };
}

function normalizeActionByRemarks(remarks) {
  return remarks == null ? "" : String(remarks);
}

export const EMPLOYEE_LEAVE_STATUSES = [
  "Pending",
  "Approved",
  "Rejected",
  "Withdrawn",
  "Revoked",
];

function dedupeLeaveRecords(records) {
  const seen = new Map();
  for (const record of records) {
    const leaveId = record?.leaveId ?? record?.id;
    if (leaveId == null) continue;
    seen.set(leaveId, record);
  }
  return Array.from(seen.values());
}

function formatLeaveExportDate(value) {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function toOptionalId(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : undefined;
}

function appendOptionalId(params, key, value) {
  const id = toOptionalId(value);
  if (id != null) {
    params.set(key, String(id));
  }
}

export const leaveService = {
  async getLeaveTypes() {
    try {
      const response = await hrmsGet("/leave-types/active", "HR16S");
      return unwrapLeaveTypes(response).filter(isActiveLeaveType).map(mapLeaveType);
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  async getLeaveBalance(empId) {
    try {
      const id = resolveEmpId(empId);
      if (id == null) {
        throw new Error("Employee ID is required to fetch leave balance.");
      }
      const response = await essPost(`/leaves/balance/${id}`, undefined, "ESS03S");
      return unwrapLeaveBalances(response).map(mapLeaveBalance);
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  async applyLeave(payload) {
    const apiPayload = {
      leaveTypeId: Number(payload.leaveTypeId),
      startDate: payload.startDate,
      endDate: payload.endDate,
      leaveReason: payload.leaveReason,
      leavesDetail: payload.leavesDetail.map((d) => ({
        date: d.date,
        firstHalf: d.firstHalf,
        secondHalf: d.secondHalf,
      })),
    };

    return await essPost("/leaves/apply", apiPayload, "ESS03A");
  },

  async getLeavesByStatus(status, { page = 0, size = 10, managerId } = {}) {
    try {
      const params = new URLSearchParams({
        leaveStatus: status,
        page: String(page),
        size: String(size),
      });
      appendOptionalId(params, "managerId", managerId);

      return await essGet(`/leaves/search/status?${params.toString()}`, "ESS03S");
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  async getEmployeeLeavesByStatus(empId, { status = "all", page = 0, size = 10 } = {}) {
    try {
      const response = await this.getEmployeeLeaveHistory(empId, {
        page,
        size: Math.max(size, 10),
        sortBy: "createdOn",
        sortDir: "DESC",
      });

      let records = dedupeLeaveRecords(extractLeaveRecords(response));

      if (status !== "all") {
        const normalizedStatus = String(status).toLowerCase();
        records = records.filter(
          (record) => String(record.leaveStatus || "").toLowerCase() === normalizedStatus
        );
      }

      return records;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  async getLeavesByLeaveTypeId(leaveTypeId) {
    try {
      return await essGet(
        `/leaves/search/leave_type_id?leaveTypeId=${leaveTypeId}&page=0&size=10`,
        "ESS03S"
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  async getEmployeeLeaveRequests(empId) {
    return this.getEmployeeLeaveHistory(empId);
  },

  async getEmployeeLeaveHistory(empId, { page = 0, size = 10, sortBy = "createdOn", sortDir = "DESC" } = {}) {
    const params = new URLSearchParams({
      page: String(page),
      size: String(size),
      sortBy,
      sortDir,
    });
    const query = `?${params}`;
    const id = toOptionalId(empId);
    const path = id != null ? `/leaves/employee/${id}` : "/leaves/employee";

    try {
      return await essGet(`${path}${query}`, "ESS03S");
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  async approveLeaveRequest(leaveId, remarks) {
    return await essPut(
      "/leaves/update",
      {
        leaveId: Number(leaveId),
        leaveStatus: "Approved",
        actionByRemarks: normalizeActionByRemarks(remarks),
      },
      "ESS03U"
    );
  },

  async rejectLeaveRequest(leaveId, remarks) {
    return await essPut(
      "/leaves/update",
      {
        leaveId: Number(leaveId),
        leaveStatus: "Rejected",
        actionByRemarks: normalizeActionByRemarks(remarks),
      },
      "ESS03U"
    );
  },

  async withdrawLeaveRequest(leaveId, remarks) {
    return await essPut(
      "/leaves/update",
      {
        leaveId: Number(leaveId),
        leaveStatus: "Withdrawn",
        actionByRemarks: normalizeActionByRemarks(remarks),
      },
      "ESS03U"
    );
  },

  async revokeLeaveRequest(leaveId, { revocationReason, leavesDetail }) {
    return await essPut(
      "/leaves/update",
      {
        leaveId: Number(leaveId),
        leaveStatus: "Revoked",
        actionByRemarks: normalizeActionByRemarks(revocationReason),
        revocationReason,
        leavesDetail: leavesDetail.map((detail) => ({
          date: detail.date,
          firstHalf: detail.firstHalf,
          secondHalf: detail.secondHalf,
        })),
      },
      "ESS03U"
    );
  },

  /**
   * POST /leaves/employee/export
   * Body: { fromDate?, toDate?, requestType: "Team" }
   * Downloads team leave requests as an Excel file.
   */
  async exportTeamLeaveRequests({
    fromDate,
    toDate,
    requestType = "Team",
    fallbackFilename = "Team_Leave_Requests.xlsx",
  } = {}) {
    const payload = { requestType };

    const formattedFromDate = formatLeaveExportDate(fromDate);
    const formattedToDate = formatLeaveExportDate(toDate);

    if (formattedFromDate) payload.fromDate = formattedFromDate;
    if (formattedToDate) payload.toDate = formattedToDate;

    const { blob, contentDisposition } = await essPostBlob(
      "/leaves/employee/export",
      payload,
      "ESS03A"
    );

    const filename = parseContentDispositionFilename(contentDisposition, fallbackFilename);
    downloadBlob({ blob, filename });

    return { filename };
  },
};

export function getLeaveDaysCount(
  startDate,
  endDate,
  leavesDetail
) {
  if (leavesDetail && Array.isArray(leavesDetail) && leavesDetail.length > 0) {
    return leavesDetail.reduce((sum, d) => {
      let days = 0;
      if (d.firstHalf === "Y") days += 0.5;
      if (d.secondHalf === "Y") days += 0.5;
      return sum + days;
    }, 0);
  }
  const parseDate = (s) => {
    const parts = s.split("-");
    if (parts.length === 3) {
      return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    }
    return new Date(s);
  };
  const fD = parseDate(startDate);
  const tD = parseDate(endDate);
  return differenceInCalendarDays(tD, fD) + 1;
}

export function getLeaveDuration(record) {
  if (!record || typeof record !== "object") return 0;

  const directDuration =
    parseDurationValue(record.leaveDays) ??
    parseDurationValue(record.leaveDuration) ??
    parseDurationValue(record.totalLeaveDays) ??
    parseDurationValue(record.numberOfDays) ??
    parseDurationValue(record.noOfDays) ??
    parseDurationValue(record.duration);

  if (directDuration != null) return directDuration;

  return getLeaveDaysCount(record.startDate, record.endDate, record.leavesDetail);
}

export function generateLeavesDetail(fromDate, toDate, fromSession, toSession) {
  if (!fromDate || !toDate || !fromSession || !toSession) return [];

  const formatDateStr = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const start = new Date(fromDate);
  start.setHours(12, 0, 0, 0);
  const end = new Date(toDate);
  end.setHours(12, 0, 0, 0);

  const dates = [];
  let current = new Date(start);
  while (current <= end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return dates.map((d) => {
    const dateStr = formatDateStr(d);
    const isFromDate = dateStr === formatDateStr(fromDate);
    const isToDate = dateStr === formatDateStr(toDate);

    let firstHalf = "Y";
    let secondHalf = "Y";

    if (isFromDate && isToDate) {
      if (fromSession === "First Half" && toSession === "First Half") {
        firstHalf = "Y";
        secondHalf = "N";
      } else if (fromSession === "Second Half" && toSession === "Second Half") {
        firstHalf = "N";
        secondHalf = "Y";
      } else if (fromSession === "First Half" && toSession === "Second Half") {
        firstHalf = "Y";
        secondHalf = "Y";
      }
    } else if (isFromDate) {
      if (fromSession === "First Half") {
        firstHalf = "Y";
        secondHalf = "Y";
      } else if (fromSession === "Second Half") {
        firstHalf = "N";
        secondHalf = "Y";
      }
    } else if (isToDate) {
      if (toSession === "First Half") {
        firstHalf = "Y";
        secondHalf = "N";
      } else if (toSession === "Second Half") {
        firstHalf = "Y";
        secondHalf = "Y";
      }
    }

    return {
      date: dateStr,
      firstHalf,
      secondHalf,
    };
  });
}

export function checkLeaveOverlap(newLeavesDetail, existingRequests) {
  if (!newLeavesDetail || newLeavesDetail.length === 0 || !existingRequests) return null;

  for (const req of existingRequests) {
    const status = req.leaveStatus?.toUpperCase();
    if (status !== "APPROVED" && status !== "PENDING") {
      continue;
    }

    for (const newDay of newLeavesDetail) {
      const dateStr = newDay.date;
      const start = req.startDate;
      const end = req.endDate;

      if (dateStr >= start && dateStr <= end) {
        let firstHalfCovered;
        let secondHalfCovered;

        if (req.leavesDetail && req.leavesDetail.length > 0) {
          const detail = req.leavesDetail.find((d) => d.date === dateStr);
          if (detail) {
            firstHalfCovered = detail.firstHalf === "Y";
            secondHalfCovered = detail.secondHalf === "Y";
          } else {
            firstHalfCovered = true;
            secondHalfCovered = true;
          }
        } else {
          firstHalfCovered = true;
          secondHalfCovered = true;
        }

        if ((newDay.firstHalf === "Y" && firstHalfCovered) || (newDay.secondHalf === "Y" && secondHalfCovered)) {
          return "Selected date and session overlap with an existing leave request.";
        }
      }
    }
  }

  return null;
}
